import { What3WordsInput } from '@/components/What3WordsInput';
import { getCustomer, listPetsByCustomer, updateCustomer } from '@/lib/api';
import type { Customer, PetWithVaccinations, UpdateCustomerInput } from '@/lib/types';
import { ArrowLeft, Loader2, PawPrint } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

function Field({
  id,
  label,
  optional,
  children,
}: {
  id: string;
  label: string;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
      >
        {label}
        {optional && (
          <span className="ml-1 normal-case tracking-normal text-muted-foreground/60">
            (optional)
          </span>
        )}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  'h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30';
const selectCls =
  'h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30';

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  region: string;
  postcode: string;
  country: string;
  what3words: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelationship: string;
  preferredContactMethod: string;
  marketingSource: string;
  internalNotes: string;
}

function fromCustomer(c: Customer): FormState {
  return {
    firstName: c.firstName ?? '',
    lastName: c.lastName ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    addressLine1: c.addressLine1 ?? '',
    addressLine2: c.addressLine2 ?? '',
    city: c.city ?? '',
    region: c.region ?? '',
    postcode: c.postcode ?? '',
    country: c.country ?? '',
    what3words: c.what3words ?? '',
    emergencyContactName: c.emergencyContactName ?? '',
    emergencyContactPhone: c.emergencyContactPhone ?? '',
    emergencyContactRelationship: c.emergencyContactRelationship ?? '',
    preferredContactMethod: c.preferredContactMethod ?? '',
    marketingSource: c.marketingSource ?? '',
    internalNotes: c.internalNotes ?? '',
  };
}

function toNullable(s: string): string | null {
  return s.trim() || null;
}

export function CustomerEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [pets, setPets] = useState<PetWithVaccinations[]>([]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [c, petsData] = await Promise.all([
        getCustomer(id),
        listPetsByCustomer(id).catch(() => []),
      ]);
      setCustomer(c);
      setForm(fromCustomer(c));
      setPets(petsData);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  function set(field: keyof FormState, value: string) {
    setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form || !id) return;
    setBusy(true);
    setError(null);
    try {
      const trimmedFirst = form.firstName.trim();
      const trimmedLast = form.lastName.trim();
      const patch: UpdateCustomerInput = {
        ...(trimmedFirst ? { firstName: trimmedFirst } : {}),
        ...(trimmedLast ? { lastName: trimmedLast } : {}),
        email: toNullable(form.email),
        phone: toNullable(form.phone),
        addressLine1: toNullable(form.addressLine1),
        addressLine2: toNullable(form.addressLine2),
        city: toNullable(form.city),
        region: toNullable(form.region),
        postcode: toNullable(form.postcode),
        country: toNullable(form.country),
        what3words: toNullable(form.what3words),
        emergencyContactName: toNullable(form.emergencyContactName),
        emergencyContactPhone: toNullable(form.emergencyContactPhone),
        emergencyContactRelationship: toNullable(form.emergencyContactRelationship),
        preferredContactMethod:
          (form.preferredContactMethod as 'email' | 'sms' | 'phone' | 'whatsapp' | '') || null,
        marketingSource: toNullable(form.marketingSource),
        internalNotes: toNullable(form.internalNotes),
      };
      const updated = await updateCustomer(id, patch);
      setCustomer(updated);
      navigate(`/customers/${id}`);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: unknown }).message)
          : 'Something went wrong';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-10 md:px-10">
        <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (notFound || !customer || !form) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-10 md:px-10">
        <Link
          to="/customers"
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground press"
        >
          <ArrowLeft className="size-4" strokeWidth={1.75} />
          Customers
        </Link>
        <div className="rounded-xl border border-border bg-surface px-6 py-12 text-center">
          <p className="mb-1 font-medium">Customer not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-10 md:px-10">
      <Link
        to={`/customers/${id}`}
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground press"
      >
        <ArrowLeft className="size-4" strokeWidth={1.75} />
        {customer.firstName} {customer.lastName}
      </Link>

      <h1 className="mb-6 font-heading text-2xl font-medium tracking-tight">
        Edit — {customer.firstName} {customer.lastName}
      </h1>

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-8">
        {/* Contact info */}
        <section>
          <h2 className="mb-4 border-b border-border pb-2 text-sm font-semibold text-foreground/80">
            Contact info
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field id="firstName" label="First name">
              <input
                id="firstName"
                type="text"
                required
                value={form.firstName}
                onChange={(e) => set('firstName', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="lastName" label="Last name">
              <input
                id="lastName"
                type="text"
                required
                value={form.lastName}
                onChange={(e) => set('lastName', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="email" label="Email" optional>
              <input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="phone" label="Phone" optional>
              <input
                id="phone"
                type="tel"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="preferredContactMethod" label="Preferred contact" optional>
              <select
                id="preferredContactMethod"
                value={form.preferredContactMethod}
                onChange={(e) => set('preferredContactMethod', e.target.value)}
                className={selectCls}
              >
                <option value="">No preference</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="sms">SMS</option>
                <option value="whatsapp">WhatsApp</option>
              </select>
            </Field>
            <Field id="marketingSource" label="How did they find us?" optional>
              <input
                id="marketingSource"
                type="text"
                value={form.marketingSource}
                onChange={(e) => set('marketingSource', e.target.value)}
                placeholder="e.g. Google, referral, walk-in"
                className={inputCls}
              />
            </Field>
          </div>
        </section>

        {/* Address */}
        <section>
          <h2 className="mb-4 border-b border-border pb-2 text-sm font-semibold text-foreground/80">
            Address
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field id="addressLine1" label="Address line 1" optional>
              <input
                id="addressLine1"
                type="text"
                value={form.addressLine1}
                onChange={(e) => set('addressLine1', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="addressLine2" label="Address line 2" optional>
              <input
                id="addressLine2"
                type="text"
                value={form.addressLine2}
                onChange={(e) => set('addressLine2', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="city" label="City / Town" optional>
              <input
                id="city"
                type="text"
                value={form.city}
                onChange={(e) => set('city', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="region" label="County / State" optional>
              <input
                id="region"
                type="text"
                value={form.region}
                onChange={(e) => set('region', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="postcode" label="Postcode / ZIP" optional>
              <input
                id="postcode"
                type="text"
                value={form.postcode}
                onChange={(e) => set('postcode', e.target.value)}
                className={`${inputCls} uppercase`}
              />
            </Field>
            <Field id="country" label="Country" optional>
              <input
                id="country"
                type="text"
                value={form.country}
                onChange={(e) => set('country', e.target.value)}
                placeholder="GB"
                className={inputCls}
              />
            </Field>
            <Field id="what3words" label="what3words" optional>
              <What3WordsInput
                id="what3words"
                value={form.what3words}
                onChange={(v) => set('what3words', v)}
                country={form.country.trim() || null}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                The exact gate or door for pickups and dropoffs.
              </p>
            </Field>
          </div>
        </section>

        {/* Emergency contact */}
        <section>
          <h2 className="mb-4 border-b border-border pb-2 text-sm font-semibold text-foreground/80">
            Emergency contact
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field id="emergencyContactName" label="Name" optional>
              <input
                id="emergencyContactName"
                type="text"
                value={form.emergencyContactName}
                onChange={(e) => set('emergencyContactName', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="emergencyContactPhone" label="Phone" optional>
              <input
                id="emergencyContactPhone"
                type="tel"
                value={form.emergencyContactPhone}
                onChange={(e) => set('emergencyContactPhone', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="emergencyContactRelationship" label="Relationship" optional>
              <input
                id="emergencyContactRelationship"
                type="text"
                value={form.emergencyContactRelationship}
                onChange={(e) => set('emergencyContactRelationship', e.target.value)}
                placeholder="e.g. Spouse, neighbour"
                className={inputCls}
              />
            </Field>
          </div>
        </section>

        {/* Staff notes */}
        <section>
          <h2 className="mb-4 border-b border-border pb-2 text-sm font-semibold text-foreground/80">
            Internal notes
            <span className="ml-1 text-xs font-normal text-muted-foreground">
              (staff-only, never shown to customers)
            </span>
          </h2>
          <textarea
            id="internalNotes"
            value={form.internalNotes}
            onChange={(e) => set('internalNotes', e.target.value)}
            rows={4}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring/30"
          />
        </section>

        {/* Pets — read-only summary; edit individual pets via their own edit pages */}
        {pets.length > 0 && (
          <section>
            <h2 className="mb-4 border-b border-border pb-2 text-sm font-semibold text-foreground/80">
              Pets
            </h2>
            <div className="flex flex-wrap gap-2">
              {pets.map((pet) => (
                <Link
                  key={pet.id}
                  to={`/customers/${id}/pets/${pet.id}/edit`}
                  className="flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-2 text-sm hover:border-primary/40 hover:shadow-sm transition-colors"
                >
                  <PawPrint className="size-3.5 text-muted-foreground" strokeWidth={1.75} />
                  <span className="font-medium">{pet.name}</span>
                  <span className="text-xs capitalize text-muted-foreground">{pet.species}</span>
                </Link>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              Click a pet to edit their details, vaccines, and vets.
            </p>
          </section>
        )}

        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={busy || !form.firstName.trim() || !form.lastName.trim()}
            className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 press disabled:opacity-60"
          >
            {busy && <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />}
            {busy ? 'Saving…' : 'Save changes'}
          </button>
          <Link
            to={`/customers/${id}`}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary press"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
